import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Save, Users, Server, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import ArchitectureSwitch from '../components/ArchitectureSwitch';

interface User {
  id: number;
  line_user_id: string;
  name: string;
  relationship: string;
  language_pref: string;
  is_active: boolean;
}

interface UserForm {
  id?: number;
  line_user_id: string;
  name: string;
  relationship: string;
  language_pref: string;
}

interface ServiceStatus {
  name: string;
  status: 'online' | 'offline' | 'unknown';
  latency?: number;
  error?: string;
  details?: any;
}

interface EnvVar {
  name: string;
  category: string;
  status: 'configured' | 'missing' | 'default';
  masked?: string;
}

interface EnvStats {
  total: number;
  configured: number;
  missing: number;
  default: number;
}

export default function Settings() {
  const [users, setUsers] = useState<User[]>([]);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [envVars, setEnvVars] = useState<EnvVar[]>([]);
  const [envStats, setEnvStats] = useState<EnvStats | null>(null);
  const [isCheckingServices, setIsCheckingServices] = useState(false);
  const [diarySettings, setDiarySettings] = useState({
    cronHour: 23,
    lastGeneration: null as string | null
  });

  // 親友管理相關 state
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserForm | null>(null);
  const [userForm, setUserForm] = useState<UserForm>({
    line_user_id: '',
    name: '',
    relationship: '',
    language_pref: 'auto'
  });

  useEffect(() => {
    fetchUsers();
    checkServices();
    fetchDiarySettings();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await apiFetch('/api/users');
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const checkServices = async () => {
    setIsCheckingServices(true);
    // 先顯示 loading 狀態
    setServices([
      { name: 'Dify AI', status: 'unknown' },
      { name: 'OpenAI', status: 'unknown' },
      { name: 'ElevenLabs', status: 'unknown' },
      { name: 'Minimax TTS', status: 'unknown' },
      { name: '中文聲音 ID', status: 'unknown' },
      { name: '英文聲音 ID', status: 'unknown' },
      { name: 'Vectorize', status: 'unknown' },
      { name: 'LINE API', status: 'unknown' },
      { name: 'Anthropic Claude', status: 'unknown' },
    ]);

    try {
      const res = await apiFetch('/api/settings/health-check');
      if (res.ok) {
        const data = await res.json();
        setServices(data.services || []);
        setEnvVars(data.envVars || []);
        setEnvStats(data.envStats || null);
      } else {
        console.error('Health check failed:', res.status);
      }
    } catch (error) {
      console.error('Health check error:', error);
    } finally {
      setIsCheckingServices(false);
    }
  };

  const fetchDiarySettings = async () => {
    try {
      const res = await apiFetch('/api/settings/diary_cron_hour');
      if (res.ok) {
        const data = await res.json();
        setDiarySettings(prev => ({
          ...prev,
          cronHour: JSON.parse(data.value)
        }));
      }

      const lastRes = await apiFetch('/api/settings/last_diary_generation');
      if (lastRes.ok) {
        const data = await lastRes.json();
        const parsed = JSON.parse(data.value);
        setDiarySettings(prev => ({
          ...prev,
          lastGeneration: parsed?.timestamp
        }));
      }
    } catch (error) {
      console.error('Failed to fetch diary settings:', error);
    }
  };

  const triggerDiaryGeneration = async () => {
    if (!confirm('確定要手動觸發今日日記生成嗎？')) return;

    try {
      const res = await apiFetch('/api/diaries/generate', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        alert(`日記生成完成！\n成功: ${data.generated} 篇\n失敗: ${data.failed} 篇`);
        fetchDiarySettings();
      }
    } catch (error) {
      alert('生成失敗');
    }
  };

  // 親友管理 CRUD 操作
  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({
      line_user_id: '',
      name: '',
      relationship: '',
      language_pref: 'auto'
    });
    setShowUserModal(true);
  };

  const openEditUser = (user: User) => {
    setEditingUser({
      id: user.id,
      line_user_id: user.line_user_id,
      name: user.name,
      relationship: user.relationship,
      language_pref: user.language_pref || 'auto'
    });
    setUserForm({
      id: user.id,
      line_user_id: user.line_user_id,
      name: user.name,
      relationship: user.relationship,
      language_pref: user.language_pref || 'auto'
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.line_user_id || !userForm.name) {
      alert('請填寫 LINE ID 和名稱');
      return;
    }

    try {
      const url = editingUser?.id ? `/api/users/${editingUser.id}` : '/api/users';
      const method = editingUser?.id ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userForm)
      });

      if (res.ok) {
        setShowUserModal(false);
        fetchUsers();
        alert(editingUser?.id ? '更新成功！' : '新增成功！');
      } else {
        const data = await res.json();
        alert(data.error || '操作失敗');
      }
    } catch (error) {
      alert('操作失敗');
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm(`確定要刪除「${user.name}」嗎？\n這會同時刪除該用戶的所有對話記錄和日記。`)) return;

    try {
      const res = await apiFetch(`/api/users/${user.id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchUsers();
        alert('刪除成功！');
      } else {
        alert('刪除失敗');
      }
    } catch (error) {
      alert('刪除失敗');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">⚙️ 系統設定</h1>

      {/* 靈魂搬家：架構切換 */}
      <ArchitectureSwitch />

      {/* 親友管理 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={20} style={{ color: 'var(--pink)' }} />
            <h2 className="text-lg font-medium">親友管理</h2>
          </div>
          <button
            onClick={openAddUser}
            className="btn-primary flex items-center gap-2 text-sm"
            style={{ padding: '8px 16px' }}
          >
            <Plus size={16} />
            新增親友
          </button>
        </div>
        <div className="space-y-2">
          {users.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              尚無親友資料
            </div>
          ) : (
            users.map(user => (
              <div
                key={user.id}
                className="flex items-center justify-between p-3 bg-bg-surface-2 rounded-lg group"
              >
                <div className="flex items-center gap-3">
                  <span className={`status-dot ${user.is_active ? 'online' : 'offline'}`}></span>
                  <div>
                    <span className="font-medium">{user.name}</span>
                    <span className="text-text-secondary text-sm ml-2">
                      {user.relationship}
                    </span>
                    <span className="text-text-muted text-xs ml-2">
                      ({user.language_pref === 'zh' ? '中文' : user.language_pref === 'en' ? '英文' : user.language_pref === 'mixed' ? '混合' : '自動'})
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-xs text-text-muted font-mono mr-4">
                    {user.line_user_id.substring(0, 12)}...
                  </div>
                  <button
                    onClick={() => openEditUser(user)}
                    className="btn-ghost p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: 'var(--text-muted)' }}
                    title="編輯"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user)}
                    className="btn-ghost p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: 'var(--red)' }}
                    title="刪除"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </motion.div>

      {/* Service status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">🔌 API 服務狀態</h2>
          <button
            onClick={checkServices}
            disabled={isCheckingServices}
            className="btn-secondary text-sm"
            style={{ padding: '6px 12px' }}
          >
            {isCheckingServices ? '檢測中...' : '🔄 重新檢測'}
          </button>
        </div>
        <div className="space-y-2">
          {services.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              點擊「重新檢測」開始檢查服務狀態
            </div>
          ) : (
            services.map(service => (
              <div
                key={service.name}
                className="flex items-center justify-between p-3 bg-bg-surface-2 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className={`status-dot ${service.status}`}></span>
                  <div>
                    <span>{service.name}</span>
                    {service.details && (
                      <div className="text-xs text-text-muted mt-0.5">
                        {service.details.voiceName && `聲音: ${service.details.voiceName}`}
                        {service.details.voiceId && !service.details.voiceName && `ID: ${service.details.voiceId.substring(0, 12)}...`}
                        {service.details.botName && `Bot: ${service.details.botName}`}
                        {service.details.indexName && `Index: ${service.details.indexName}`}
                        {service.details.subscription && `方案: ${service.details.subscription}`}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm text-text-secondary text-right">
                  {service.status === 'unknown' ? (
                    <span className="text-text-muted">檢測中...</span>
                  ) : service.status === 'online' ? (
                    <>
                      <span className="text-success">正常</span>
                      {service.latency && (
                        <span className="text-text-muted ml-2">
                          {service.latency}ms
                        </span>
                      )}
                    </>
                  ) : (
                    <div>
                      <span className="text-error">離線</span>
                      {service.error && (
                        <div className="text-xs text-text-muted mt-0.5">
                          {service.error}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </motion.div>

      {/* 環境變數監控 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="card"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Server size={20} style={{ color: 'var(--purple)' }} />
            <h2 className="text-lg font-medium">環境變數監控</h2>
          </div>
          {envStats && (
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <CheckCircle size={14} className="text-success" />
                <span className="text-success">{envStats.configured}</span>
              </div>
              <div className="flex items-center gap-1">
                <XCircle size={14} className="text-error" />
                <span className="text-error">{envStats.missing}</span>
              </div>
              <div className="flex items-center gap-1">
                <AlertCircle size={14} className="text-warning" />
                <span className="text-warning">{envStats.default}</span>
              </div>
              <span className="text-text-muted">/ {envStats.total}</span>
            </div>
          )}
        </div>

        {/* 進度條 */}
        {envStats && (
          <div className="mb-4">
            <div className="h-2 bg-bg-surface-2 rounded-full overflow-hidden flex">
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${(envStats.configured / envStats.total) * 100}%`,
                  background: 'var(--success)'
                }}
              />
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${(envStats.default / envStats.total) * 100}%`,
                  background: 'var(--warning)'
                }}
              />
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${(envStats.missing / envStats.total) * 100}%`,
                  background: 'var(--error)'
                }}
              />
            </div>
          </div>
        )}

        {/* 按分類顯示環境變數 */}
        {envVars.length === 0 ? (
          <div className="text-center py-8 text-text-muted">
            點擊「重新檢測」查看環境變數狀態
          </div>
        ) : (
          <div className="space-y-4">
            {/* 按 category 分組 */}
            {Object.entries(
              envVars.reduce((acc, env) => {
                if (!acc[env.category]) acc[env.category] = [];
                acc[env.category].push(env);
                return acc;
              }, {} as Record<string, EnvVar[]>)
            ).map(([category, vars]) => (
              <div key={category}>
                <div className="text-xs text-text-muted uppercase tracking-wider mb-2 flex items-center gap-2">
                  {category}
                  <span className="text-text-muted">
                    ({vars.filter(v => v.status === 'configured').length}/{vars.length})
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {vars.map(env => (
                    <div
                      key={env.name}
                      className="flex items-center justify-between p-2 bg-bg-surface-2 rounded-lg text-sm"
                    >
                      <div className="flex items-center gap-2">
                        {env.status === 'configured' ? (
                          <CheckCircle size={14} className="text-success" />
                        ) : env.status === 'default' ? (
                          <AlertCircle size={14} className="text-warning" />
                        ) : (
                          <XCircle size={14} className="text-error" />
                        )}
                        <span className="font-mono text-xs">{env.name}</span>
                      </div>
                      <div className="text-xs text-text-muted truncate max-w-[150px]" title={env.masked}>
                        {env.masked || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 圖例說明 */}
        <div className="mt-4 pt-4 flex items-center justify-center gap-6 text-xs text-text-muted" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="flex items-center gap-1">
            <CheckCircle size={12} className="text-success" />
            <span>已設定</span>
          </div>
          <div className="flex items-center gap-1">
            <AlertCircle size={12} className="text-warning" />
            <span>使用預設值</span>
          </div>
          <div className="flex items-center gap-1">
            <XCircle size={12} className="text-error" />
            <span>缺少</span>
          </div>
        </div>
      </motion.div>

      {/* Diary schedule */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card"
      >
        <h2 className="text-lg font-medium mb-4">📔 日記排程</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">每日生成時間</span>
            <span className="font-mono">{diarySettings.cronHour}:00 UTC+8</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">最近一次生成</span>
            <span className="text-sm">
              {diarySettings.lastGeneration ? (
                <>
                  {new Date(diarySettings.lastGeneration).toLocaleString('zh-TW')}
                  <span className="text-success ml-2">✅</span>
                </>
              ) : (
                <span className="text-text-muted">尚無記錄</span>
              )}
            </span>
          </div>
          <button
            onClick={triggerDiaryGeneration}
            className="btn-secondary w-full"
          >
            🔄 手動觸發今日日記
          </button>
        </div>
      </motion.div>

      {/* Admin accounts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card"
      >
        <h2 className="text-lg font-medium mb-4">👤 帳號管理</h2>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-bg-surface-2 rounded-lg">
            <span>felicity19990819@gmail.com</span>
            <span className="text-xs bg-pink/20 text-pink px-2 py-1 rounded">admin</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-bg-surface-2 rounded-lg">
            <span>waitinchen@gmail.com</span>
            <span className="text-xs bg-pink/20 text-pink px-2 py-1 rounded">admin</span>
          </div>
        </div>
      </motion.div>
      {/* 親友編輯 Modal */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-md rounded-2xl"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
          >
            <div className="p-6" style={{ borderBottom: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium">
                  {editingUser?.id ? '✏️ 編輯親友' : '➕ 新增親友'}
                </h2>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="btn-ghost p-2 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">
                  LINE User ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userForm.line_user_id}
                  onChange={e => setUserForm({ ...userForm, line_user_id: e.target.value })}
                  placeholder="Uxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-4 py-2 rounded-lg bg-bg-surface-2 border border-border text-text-primary"
                  disabled={!!editingUser?.id} // 編輯時不能改 LINE ID
                />
                <p className="text-xs text-text-muted mt-1">
                  LINE User ID 可從 LINE Developers 或對話 log 中取得
                </p>
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-2">
                  稱呼 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={e => setUserForm({ ...userForm, name: e.target.value })}
                  placeholder="例如：Daddy, Mommy, 威廷哥"
                  className="w-full px-4 py-2 rounded-lg bg-bg-surface-2 border border-border text-text-primary"
                />
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-2">
                  關係
                </label>
                <input
                  type="text"
                  value={userForm.relationship}
                  onChange={e => setUserForm({ ...userForm, relationship: e.target.value })}
                  placeholder="例如：父親, 母親, 朋友, 工程師夥伴"
                  className="w-full px-4 py-2 rounded-lg bg-bg-surface-2 border border-border text-text-primary"
                />
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-2">
                  語言偏好
                </label>
                <select
                  value={userForm.language_pref}
                  onChange={e => setUserForm({ ...userForm, language_pref: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-bg-surface-2 border border-border text-text-primary"
                >
                  <option value="auto">自動偵測</option>
                  <option value="zh">中文</option>
                  <option value="en">英文</option>
                  <option value="mixed">中英混合</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowUserModal(false)}
                  className="btn-secondary flex-1"
                >
                  取消
                </button>
                <button
                  onClick={handleSaveUser}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <Save size={16} />
                  儲存
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
